"use client";

import { ErrorCard } from "@/components/ui/shared/ErrorCard";
import { useUserStore } from "@/lib/stores/userStore";
import { createClient } from "@/utils/supabase/client";
import { LogIn } from "lucide-react";
import { useCallback, useState } from "react";

interface AccessDeniedCardProps {
  /** The email the visitor is currently signed in with, surfaced so they know which account to switch from. */
  viewerEmail: string;
  /** Resolved organizer contact email (page contact_email → owner → support). */
  contactEmail: string;
  /** Where to send the visitor after signing out (the page's login/SSO redirect). */
  loginRedirect: string;
}

/**
 * Access-denied screen shown when a signed-in visitor lacks access to an invited
 * landing page. Unlike `notFound()`, it tells them the page exists but their account
 * isn't on the guest list, points them at the organizer, and lets them re-auth as a
 * different account (the common cause of a denial).
 *
 * @param viewerEmail - The currently signed-in email, shown in the message.
 * @param contactEmail - Organizer email for the "contact the organizer" link.
 * @param loginRedirect - Login/SSO URL to send the visitor to after signing out.
 * @returns The rendered access-denied card.
 */
export function AccessDeniedCard({
  viewerEmail,
  contactEmail,
  loginRedirect,
}: AccessDeniedCardProps): React.ReactElement {
  // States
  const [isSwitching, setIsSwitching] = useState(false);

  // Handlers
  const handleSwitchAccount = useCallback(async () => {
    setIsSwitching(true);
    try {
      const supabase = createClient();
      await supabase.auth.signOut({ scope: "local" });
      useUserStore.getState().clearUser();
    } catch (error) {
      console.error("AccessDeniedCard: sign out failed", error);
    } finally {
      // Full navigation (not router.push) so the signed-out session is picked up
      // server-side on the next render.
      window.location.href = loginRedirect;
    }
  }, [loginRedirect]);

  // Render
  return (
    <ErrorCard
      title="Access restricted"
      message={`You're signed in as ${viewerEmail}, but this page is invite-only and your account isn't on the guest list.`}
      showBackButton={false}
      showHomeButton={false}
      actions={[
        {
          label: isSwitching ? "Signing out…" : "Sign in with a different account",
          onClick: handleSwitchAccount,
          variant: "primary",
          icon: <LogIn className="h-4 w-4" />,
        },
      ]}
      helpText="Think this is a mistake? Contact the organizer:"
      supportEmail={contactEmail}
      supportLinkText={contactEmail}
    />
  );
}
